import { IMDSSelectProps } from "./select.types";
import {
  StyledButtonContent,
  StyledListBox,
  StyledListBoxItem,
  StyledPopover,
  StyledSelect,
  StyledSelectValue,
} from "./select.style";
import { MDSButton, MDSButtonVariant } from "@/mds";
import { FieldError, ListBoxItemProps } from "react-aria-components";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { useState } from "react";
import { getUnslottedChildren } from "@/mds/utils/slots";

/**
 * MDS Select component based on react-aria Select
 * Provides an accessible select input with MDS styling
 */
export function MDSSelect(props: IMDSSelectProps) {
  const {
    label,
    errorMessage,
    children,
    defaultOpen,
    onOpenChange,
    items,
    popoverProps,
    buttonProps,
    renderSelectValue,
    buttonVariant,
    ...rest
  } = props;

  const [_isOpen, _setIsOpen] = useState(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    _setIsOpen(open);
    onOpenChange?.(open);
  };

  const mergedPopoverProps = popoverProps;

  return (
    <StyledSelect
      aria-label={label}
      data-mds-name="select"
      label={label}
      items={items}
      isOpen={_isOpen}
      onOpenChange={handleOpenChange}
      {...rest}
    >
      <MDSButton variant={buttonVariant ?? MDSButtonVariant.Transparent} {...buttonProps}>
        <StyledButtonContent>
          {(() => {
            if (renderSelectValue) {
              // Pass render function as children to SelectValue per RAC API
              return <StyledSelectValue>{props => renderSelectValue(props)}</StyledSelectValue>;
            }
            return <StyledSelectValue />;
          })()}
          <div aria-hidden="true">
            <MdsIcon kind={MdsIconKind.CaretDown} data-test-id="select-toggle-icon" />
          </div>
        </StyledButtonContent>
      </MDSButton>
      <FieldError>{errorMessage}</FieldError>
      <StyledPopover {...mergedPopoverProps}>
        {getUnslottedChildren(children)}
        {items && (
          <StyledListBox items={items}>
            {item => <MDSSelectItem key={item.id} {...item} />}
          </StyledListBox>
        )}
      </StyledPopover>
    </StyledSelect>
  );
}

export const MDSSelectItem = (props: ListBoxItemProps) => {
  return <StyledListBoxItem {...props} />;
};
